import { ReactNode, useRef } from 'react';
import {
  SidebarGroupHeader,
  SidebarFooter,
  SidebarItem,
  SidebarItemIcon,
  SidebarItemLabel,
  SidebarDivider,
  Divider,
  Box,
  SidebarProps,
  Sidebar,
  Tooltip,
  SidebarItemDateExtra,
  SkeletonBox,
  Skeleton,
  TypeIconName,
} from '@bitrise/bitkit';
import { SUBHEADER_STICKY_HEIGHT } from '@/components/SubHeader/SubHeader';
import useIsFullSidebarVisible from './useIsFullSidebarVisible';
import useSidebarHeight from './useSidebarHeight';

export type PageLayoutSidebarLink = {
  pageName: string;
  iconProps?: Parameters<typeof SidebarItemIcon>[0];
  itemProps: Omit<Parameters<typeof SidebarItem>[0], 'children' | 'isExternal'> & {
    disabled?: boolean;
    disabledTooltip?: string;
    targetDate?: string;
  };
  label: ReactNode;
};
export type PageLayoutSidebarDivider = { divider: true; label?: string };
export type PageLayoutSidebarItem =
  | PageLayoutSidebarLink
  | PageLayoutSidebarDivider
  | { skeleton: true; iconName: TypeIconName };

export type PageLayoutSidebarProps = {
  extraContent?: ReactNode;
  isMobile: boolean;
  isStickyHeader: boolean;
  items: PageLayoutSidebarItem[];
  footer?: ReactNode;
  platformShell: {
    headerHeight: number;
    navWidth: number;
  };
  selectedPage: string;
  subHeaderRef: React.RefObject<HTMLDivElement>;
  width: '16rem' | '20rem';
};

const SidebarContent = ({
  extraContent,
  footerRef,
  headerRef,
  isMobile,
  isFullSidebarVisible,
  items,
  footer,
  platformShell,
  selectedPage,
}: PageLayoutSidebarProps & {
  isFullSidebarVisible: boolean;
  footerRef: React.RefObject<HTMLDivElement>;
  headerRef: React.RefObject<HTMLDivElement>;
}) => {
  const headerProps: Partial<SidebarProps> =
    !isFullSidebarVisible || isMobile
      ? {}
      : {
          position: 'sticky' as const,
          top: `${platformShell.headerHeight + SUBHEADER_STICKY_HEIGHT}px`,
        };

  const footerProps: Partial<SidebarProps> =
    !isFullSidebarVisible || isMobile
      ? {}
      : {
          position: 'sticky' as const,
          bottom: 0,
        };

  return (
    <>
      <Box ref={headerRef} {...headerProps} paddingTop="24">
        {items.map((item, index) => {
          if ('divider' in item) {
            if (item.label) {
              return <SidebarGroupHeader key={item.label} label={item.label} />;
            }
            // eslint-disable-next-line react/no-array-index-key
            return <SidebarDivider key={index} />;
          }

          if ('skeleton' in item) {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={index} isActive>
                <SidebarItem>
                  <SidebarItemIcon name={item.iconName} />
                  <SidebarItemLabel>
                    <SkeletonBox height="20px" width="150px" />
                  </SidebarItemLabel>
                </SidebarItem>
              </Skeleton>
            );
          }

          const isSelected = selectedPage === item.pageName && !isMobile;

          const { disabledTooltip, ...itemProps } = item.itemProps;

          return (
            <Tooltip
              key={item.pageName}
              label={disabledTooltip}
              placement="right"
              isDisabled={!item.itemProps.disabled}
            >
              <SidebarItem {...itemProps} selected={isSelected} width="100%">
                {item.iconProps && (
                  <SidebarItemIcon
                    {...item.iconProps}
                    color={isSelected ? 'icon/interactive' : 'inherit'}
                    key={`${item.pageName}-icon`}
                  />
                )}
                <SidebarItemLabel key={`${item.pageName}-label`}>{item.label}</SidebarItemLabel>
                {item.itemProps.targetDate && <SidebarItemDateExtra date={new Date(item.itemProps.targetDate)} />}
              </SidebarItem>
            </Tooltip>
          );
        })}
        {extraContent && (
          <>
            <Divider marginY={[16, 24]} marginX="32" w="auto" />

            {extraContent}
          </>
        )}
      </Box>
      {footer && (
        <Box backgroundColor="neutral.100" {...footerProps} paddingBottom="12" ref={footerRef}>
          <SidebarFooter>{footer}</SidebarFooter>
        </Box>
      )}
    </>
  );
};

/**
 * When full sidebar is visible the header and footer parts of the sidebar are sticky separately
 * (header is sticky to the top, footer is to the bottom).
 * When full sidebar is not visible, sidebar has an internal scrollbar and the header and footer
 * parts of the sidebar are not separated but sticky to the top as one unit.
 */
const PageLayoutSidebar = (props: PageLayoutSidebarProps) => {
  const { isMobile, isStickyHeader, platformShell, subHeaderRef, width } = props;

  const sidebarRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const { isFullSidebarVisible } = useIsFullSidebarVisible({
    isStickyHeader,
    platformShellHeaderHeight: platformShell.headerHeight,
    footerRef,
    headerRef,
    sidebarRef,
    subHeaderRef,
  });

  const { sidebarHeight } = useSidebarHeight({
    isStickyHeader,
    subHeaderRef,
    sidebarRef,
  });

  return (
    <Sidebar
      ref={sidebarRef}
      data-testid="page-navigation-sidebar"
      minW={isMobile ? '100%' : width}
      left={!isMobile ? platformShell.navWidth : '0'}
      backgroundColor="neutral.100"
      borderRight="1px solid"
      borderRightColor="separator.primary"
      sx={
        isFullSidebarVisible || isMobile
          ? {}
          : {
              height: sidebarHeight,
              position: 'sticky' as const,
              overflowY: 'auto',
              top: `${platformShell.headerHeight + SUBHEADER_STICKY_HEIGHT}px`,
              zIndex: 100,
            }
      }
    >
      {isFullSidebarVisible && !isMobile ? (
        <Box display="flex" flex="1" flexDir="column" justifyContent="space-between">
          <SidebarContent
            {...props}
            footerRef={footerRef}
            headerRef={headerRef}
            isFullSidebarVisible={isFullSidebarVisible}
          />
        </Box>
      ) : (
        <SidebarContent
          {...props}
          footerRef={footerRef}
          headerRef={headerRef}
          isFullSidebarVisible={isFullSidebarVisible}
        />
      )}
    </Sidebar>
  );
};

export default PageLayoutSidebar;
